import { useCallback, useEffect, useRef } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { tradeSlice, DEFAULT_TRADE_STATE } from "../store/trade-slice"

const PREFERENCE_SUBTYPE = "trade"
const PREFERENCE_FIELDS = ["isExpanded"]

export function useSyncTradePreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()

  const initialDashboardIdRef = useRef(dashboardId)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && dashboardId !== initialDashboardIdRef.current) {
      throw new Error(
        `useSyncTradePreferences: dashboardId changed from "${initialDashboardIdRef.current}" to "${dashboardId}". `
        + "dashboardId must be stable for the component lifetime. "
        + "Unmount and remount DashboardLayout when navigating between dashboards, "
        + "or implement resetKey support in usePreferences.",
      )
    }
  }, [dashboardId])
  const state = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)

  const setPreferencesAction = useCallback(
    (prefs: { isExpanded?: boolean }) =>
      tradeSlice.actions.setPreferences({ dashboardId, ...prefs }),
    [dashboardId],
  )

  const isPreferencesRequestLoaded = usePreferences(PREFERENCE_SUBTYPE, PREFERENCE_FIELDS, state, dispatch, setPreferencesAction)

  // When the user has no saved preferences, usePreferences flips its internal
  // loaded flag true WITHOUT dispatching (preferencesToApply is {}), so the
  // slice instance stays uninitialised. Dispatch once to populate the instance
  // with DEFAULT_TRADE_STATE and set isPreferencesLoaded=true.
  useEffect(() => {
    if (isPreferencesRequestLoaded && !state.isPreferencesLoaded) {
      dispatch(tradeSlice.actions.setPreferences({ dashboardId }))
    }
  }, [isPreferencesRequestLoaded, state.isPreferencesLoaded, dashboardId, dispatch])

  // Return the slice flag, not usePreferences' internal flag — so the gate
  // stays closed until s.dashboard.trade[dashboardId] is actually populated
  // (otherwise consumers see DEFAULT_TRADE_STATE for a render).
  return state.isPreferencesLoaded
}
